import React, { useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import * as AuditService from '~/services/AuditService';

import { AuditLogResponse, AuditLog as IAuditLog } from '~/types/responses';

import { AuditLog } from '../AuditLog';

export interface AuditsModalProps {
  id: string;
  type?: string;
  fetchType?: 'keyValue' | 'modelAndId' | 'model' | 'url';
  pageSize?: number;
  buttonMarginTop?: number;
}

export function AuditsModal({ id, type, fetchType = 'url', pageSize = 50, buttonMarginTop = 2 }: AuditsModalProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(pageSize);
  const [total, setTotal] = useState(0);
  const [audits, setAudits] = useState<Array<IAuditLog>>([]);
  const [filter, setFilter] = useState<string | null>(null);

  const possibleFilters = useMemo(() => {
    return [...new Set(audits.flatMap((a) => [a.action, ...a.url.split('/').filter((u) => u !== '')]))];
  }, [audits]);

  const filteredAudits = useMemo(() => {
    if (!filter) {
      return audits;
    }
    return audits.filter((a) => a.action === filter || a.url.includes(filter));
  }, [audits, filter]);

  useEffect(() => {
    // reset on close
    if (!open) {
      setFilter(null);
      setPage(1);
      setLimit(pageSize);
      setAudits([]);
      setTotal(0);
      setError(null);
      return;
    }

    // fetch on open
    const getAudits = async () => {
      setFilter(null);
      let response: AuditLogResponse;
      switch (fetchType) {
        case 'keyValue':
          response = await AuditService.getLogsByKeyValue(id, type, page, limit);
          break;
        case 'modelAndId':
          response = await AuditService.getLogsByModelAndId(type, id, page, limit);
          break;
        case 'model':
          response = await AuditService.getLogsByModel(type, page, limit);
          break;
        case 'url':
        default:
          response = await AuditService.getLogsByUrl(id, page, limit);
          break;
      }
      setAudits(response.result);
      setTotal(parseInt(response.total ?? response.message.split(' ')[1]));
    };
    setLoading(true);
    getAudits()
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        setError(error.message);
        setAudits([]);
        setLoading(false);
      });
  }, [fetchType, id, limit, open, page, type]);

  return (
    <>
      <Button
        variant="outlined"
        color="error"
        sx={{ mt: buttonMarginTop }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setOpen(true);
        }}
      >
        Audits
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>
          <Typography variant="h6">
            Audits for {id}
            {type && ` (${type})`}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {loading && <LinearProgress />}
          {total > 0 && (
            <Stack direction="row" justifyContent="space-between" alignItems="center" my={2}>
              <Typography>
                Showing {filter ? filteredAudits.length : audits.length} of {total} audits
              </Typography>
              <Pagination
                count={Math.ceil(total / limit)}
                page={page}
                onChange={(_, value) => setPage(value)}
                sx={{ justifySelf: 'flex-end' }}
              />
            </Stack>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          {filteredAudits.length > 0 && (
            <>
              <Stack sx={{ justifySelf: 'flex-end' }} direction="row" spacing={2} my={2}>
                <Select
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value))}
                  sx={{ minWidth: 100 }}
                  size="small"
                >
                  <MenuItem value={10}>10 per page</MenuItem>
                  <MenuItem value={25}>25 per page</MenuItem>
                  <MenuItem value={50}>50 per page</MenuItem>
                  <MenuItem value={100}>100 per page</MenuItem>
                </Select>
                <Select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  sx={{ minWidth: 100 }}
                  size="small"
                  disabled={filteredAudits.length === 0}
                  title="Filters (only applies to current page of results)"
                >
                  <MenuItem value={null}>All</MenuItem>
                  {possibleFilters.map((f) => (
                    <MenuItem key={f} value={f}>
                      {f}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
              <Stack spacing={2} sx={{ overflowY: 'auto', maxHeight: '60vh' }}>
                {filteredAudits.map((a) => (
                  <AuditLog key={a.id} auditLog={a} />
                ))}
              </Stack>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
